
import { Component, Vue, Watch } from "vue-property-decorator";
import Booking from "../../domain/booking/Booking";
import BookingList from "@/components/booking/BookingList.vue";
import BookingViewDatePicker from "@/components/booking/components/BookingViewDatePicker.vue";
import RemoteServices from "@/services/RemoteServices";
import TimeService from "@/services/TimeService";
import Restaurant from "@/domain/restaurant/Restaurant";
import BookingManualOverrideDialog from "@/components/booking/manualOverride/BookingManualOverrideDialog.vue";
import WorkingHours from "@/domain/restaurant/WorkingHours";
import WaitingListDialog from "@/components/booking/waitingList/WaitingListDialog.vue";
import BookingsByTableDialog from "@/components/booking/bookingsByTable/BookingsByTableDialog.vue";
import SearchBookingDialog from "@/components/booking/search/SearchBookingDialog.vue";
import Notice from "@/domain/restaurant/Notice";
import NoticeDialog from "@/components/restaurant/notice/NoticeDialog.vue";

@Component({
  components: {
    NoticeDialog,
    SearchBookingDialog,
    BookingsByTableDialog,
    BookingManualOverrideDialog,
    WaitingListDialog,
    BookingViewDatePicker,
    BookingList
  }
})
export default class BookingView extends Vue {
  showManualOverrideDialog = false;

  bookingList: Booking[] = [];

  selectedDate = new Date();

  loadingTable = false;

  groupByRoom = true;

  showBookingsByTableDialog = false;

  noticeDialog = false;

  noticeList: Notice[] = [];

  noticeToDisplayIndex = 0;

  showSearchBookingDialog = false;

  showInactive = false;

  workingHours: WorkingHours | null = null;

  workingHoursList: WorkingHours[] = [];

  showMenu = false;

  waitingListSnackbar = false;

  waitingListDialog = false;

  refreshBookingTimeout = false;

  dateBeforeTimeout: Date = this.selectedDate;

  hideWaitingList = false;

  timeout: ReturnType<typeof setTimeout> = setTimeout(() => {}, 1);

  async created() {
    this.updateWorkingHours();
  }

  async mounted() {
    if (!this.hasRestaurants) await this.$router.push("Restaurants");
    await this.getNotices();
    await this.refreshBookings();
  }

  showNoticeDialog() {
    this.noticeDialog = true;
  }

  get isManagerOrAdmin() {
    return this.$store.getters.isManager || this.$store.getters.isAdmin;
  }

  get restaurantList() {
    return this.$store.getters.getRestaurantList;
  }

  get hasRestaurants() {
    return this.restaurantList.length > 0;
  }

  /* Removes Waiting List */
  get filteredBookingList() {
    return this.bookingList.filter(b => !b.isOnWaitingList());
  }

  openWaitingListDialog() {
    this.waitingListDialog = true;
    this.waitingListSnackbar = false;
  }

  get waitingList() {
    return this.bookingList.filter(b => b.isOnWaitingList());
  }

  get bookingListComponent(): BookingList {
    return this.$refs.BookingList as BookingList;
  }

  updateDateAndFetch(newDate: Date) {
    this.updateDate(newDate);
    this.startChangeDateTimeout();
  }

  updateDate(newDate: Date) {
    this.selectedDate.setHours(0, 0, 0, 0);
    newDate.setHours(0, 0, 0, 0);
    if (this.selectedDate.getTime() !== newDate.getTime())
      this.selectedDate = newDate;
  }

  displayManualOverrideDialog() {
    this.showManualOverrideDialog = true;
    this.showMenu = false;
  }

  resetRefreshTimeout() {
    this.refreshBookingTimeout = false;
  }

  startRefreshTimeout() {
    this.refreshBookingTimeout = true;
    setTimeout(this.resetRefreshTimeout, 2000);
  }

  startChangeDateTimeout() {
    clearTimeout(this.timeout);
    this.bookingList = [];
    this.loadingTable = true;
    this.dateBeforeTimeout = new Date(this.selectedDate);
    this.timeout = setTimeout(this.refreshIfDateNotChanged, 500);
  }

  refreshIfDateNotChanged() {
    if (this.dateBeforeTimeout.getTime() === this.selectedDate.getTime()) {
      this.dateBeforeTimeout = new Date();
      this.refreshBookings();
    }
  }

  @Watch("selectedRestaurant")
  updateWorkingHours() {
    const restaurant = this.selectedRestaurant;
    if (
      restaurant != null &&
      restaurant.workingHours != null &&
      restaurant.workingHours.length > 0
    ) {
      this.workingHoursList = restaurant.workingHours;
      this.workingHours = restaurant.workingHours[0];
    } else {
      this.workingHoursList = [];
      this.workingHours = null;
    }
  }

  newBooking(booking: Booking | null) {
    if (booking != null && booking.bookingDateTime != null) {
      this.hideWaitingList = true;
      this.updateDateAndFetch(new Date(booking.bookingDateTime));
    }
  }

  newNotice(notice: Notice | null) {
    if (notice != null) this.noticeList.push(notice);
  }

  removeNotice(notice: Notice | null) {
    this.noticeList = [];
  }

  get hasSelectedRestaurant() {
    return this.selectedRestaurant != null;
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$store.getters.getSelectedRestaurant;
  }

  checkWaitingList() {
    if (
      !this.hideWaitingList &&
      this.bookingList.some(b => b.isOnWaitingList())
    )
      this.waitingListSnackbar = true;
    this.hideWaitingList = false;
  }

  goToBooking(booking: Booking | null) {
    if (booking != null && booking.bookingDateTime != null) {
      this.updateDateAndFetch(new Date(booking.bookingDateTime));
    }
  }

  @Watch("workingHours")
  async getNotices() {
    if (this.hasSelectedRestaurant && this.selectedDate != null) {
      this.noticeList = [];
      try {
        this.noticeList = await RemoteServices.getNoticesByDate(
          this.selectedRestaurant!,
          TimeService.getISODateString(this.selectedDate)
        );
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
    }
  }

  @Watch("workingHours")
  async refreshBookings() {
    if (this.hasSelectedRestaurant && this.workingHours != null) {
      this.loadingTable = true;
      this.noticeList = [];
      this.bookingList = [];
      this.waitingListSnackbar = false;
      try {
        this.bookingList = await RemoteServices.getBookingsByDateAtWorkingHours(
          this.workingHours,
          TimeService.getISODateString(this.selectedDate)
        );
        await this.getNotices();
        this.checkWaitingList();
        this.startRefreshTimeout();
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      this.loadingTable = false;
    }
  }

  get noticeToDisplay(): Notice | null {
    if (this.noticeList.length === 0) return null;
    return this.noticeList[0];
  }
}
